import { AxiosError } from 'axios';
import { ApiResponse } from '@/shared/api/types';

/**
 * API 에러 응답에서 사용자에게 표시할 에러 메시지를 추출하는 함수
 * @param error - API 에러 객체
 * @returns 사용자에게 표시할 에러 메시지
 */
export const extractErrorMessage = (error: unknown): string => {
  // 기본 에러 메시지
  let errorMessage = '요청 처리에 실패했습니다.';

  if (error && typeof error === 'object') {
    // 응답이 없는 AxiosError(네트워크 단절/타임아웃/CORS 차단)를 먼저 걸러낸다.
    // AxiosError 는 Error 를 상속해 message 를 항상 가지므로, 아래 ApiResponse 분기로
    // 흘려보내면 'Network Error' 같은 영문 원문이 그대로 사용자에게 노출된다.
    if (error instanceof AxiosError && !error.response) {
      if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
        return '요청 시간이 초과되었습니다. 잠시 후 다시 시도해 주세요.';
      }
      return '네트워크 연결이 불안정합니다. 연결 상태를 확인한 뒤 다시 시도해 주세요.';
    }

    // ApiResponse 형태의 에러인 경우
    if ('error' in error || 'data' in error || 'message' in error) {
      const apiError = error as ApiResponse<Record<string, string>>;

      // data 객체에 필드별 검증 에러 메시지가 있으면 우선 사용
      if (apiError.data && typeof apiError.data === 'object') {
        const firstErrorValue = Object.values(apiError.data)[0];
        if (typeof firstErrorValue === 'string' && firstErrorValue) {
          errorMessage = firstErrorValue;
        }
      }

      // data에 메시지가 없으면 error.message 사용
      if (errorMessage === '요청 처리에 실패했습니다.' && apiError.error?.message) {
        errorMessage = apiError.error.message;
      } else if (errorMessage === '요청 처리에 실패했습니다.' && apiError.message) {
        errorMessage = apiError.message;
      }
    }
    // AxiosError인 경우
    else if (error instanceof AxiosError && error.response?.data) {
      const responseData = error.response.data as ApiResponse<Record<string, string>>;
      if (responseData.data && typeof responseData.data === 'object') {
        const firstErrorValue = Object.values(responseData.data)[0];
        if (typeof firstErrorValue === 'string' && firstErrorValue) {
          errorMessage = firstErrorValue;
        }
      }
      if (errorMessage === '요청 처리에 실패했습니다.' && responseData.error?.message) {
        errorMessage = responseData.error.message;
      } else if (errorMessage === '요청 처리에 실패했습니다.' && responseData.message) {
        errorMessage = responseData.message;
      }
    }
    // 일반 Error 객체인 경우
    else if (error instanceof Error && error.message) {
      errorMessage = error.message;
    }
  }

  return errorMessage;
};


/**
 * API 호출 시 발생하는 에러를 중앙에서 처리하는 유틸리티 함수
 *
 * @param apiCall - 실행할 API 호출 함수
 * @param errorContext - 에러 발생 시 로그에 표시할 컨텍스트 메시지
 * @returns API 호출 결과
 *
 * @example
 * ```typescript
 * export const getBooking = async (id: string) => {
 *   return withApiErrorHandler(
 *     () => apiClient.get<ApiResponse<Booking>>(`/v1/bookings/${id}`),
 *     '예약 정보 가져오기'
 *   );
 * };
 * ```
 */
export const withApiErrorHandler = async <T>(apiCall: () => Promise<T>, errorContext: string): Promise<T> => {
  try {
    return await apiCall();
  } catch (error) {
    // 에러 로깅
    if (error instanceof AxiosError) {
      if (error.response) {
        // HTTP 응답 에러 (4xx, 5xx)
        console.error(`${errorContext} 실패:`, error.response.data);
        throw error.response.data;
      } else if (error.request) {
        // 네트워크 에러
        console.error(`${errorContext} 네트워크 오류:`, error);
        throw error;
      }
    }

    // 기타 에러
    console.error(`${errorContext} 실패:`, error);
    throw error;
  }
};

/**
 * API 응답에서 data 필드를 추출하는 헬퍼 함수
 *
 * @param apiCall - ApiResponse<T> 타입을 반환하는 API 호출 함수
 * @param errorContext - 에러 컨텍스트 메시지
 * @returns API 응답의 data 필드
 *
 * @example
 * ```typescript
 * export const getBooking = async (id: string) => {
 *   return extractApiData(
 *     () => apiClient.get<ApiResponse<Booking>>(`/v1/bookings/${id}`),
 *     '예약 정보 가져오기'
 *   );
 * };
 * ```
 */
export const extractApiData = async <T>(
  apiCall: () => Promise<{ data: ApiResponse<T> }>,
  errorContext: string,
): Promise<T> => {
  const response = await withApiErrorHandler(apiCall, errorContext);
  return response.data.data;
};
